import { Add, Edit } from '@mui/icons-material'
import { Box, ClickAwayListener, Stack, Typography } from '@mui/material'
import isEqual from 'lodash/isEqual'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { useHover } from '@omnea-app/fe-browser-utils/useHover'
import { UserAutocomplete } from '@omnea-app/fe-users-and-groups/UserAutocomplete'
import { UserItem } from '@omnea-app/fe-users-and-groups/UserItem'
import { ProfileBox } from '@omnea-app/frontend/components/shared/ui/profile-box/ProfileBox'
import { useUpdateRequestProperties } from '../../helpers/useUpdateRequestProperties'

type RequestOwnerVariant = 'REQUEST_TABLE_COLUMN' | 'REQUEST_DETAILS_PAGE'
interface RequestOwnerProps {
  requestId: string
  owner: UserItem | null
  variant: RequestOwnerVariant
}

const AddProjectOwnerActionItem = ({
  editProjectOwner,
  variant,
}: {
  editProjectOwner: () => void
  variant: RequestOwnerVariant
}) => {
  const { t } = useTranslation('requests')
  const isRequestDetailsVariant = isEqual(variant, 'REQUEST_DETAILS_PAGE')
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      onClick={(event) => {
        event.preventDefault()
        editProjectOwner()
      }}
      sx={{
        width: '100%',
        minHeight: '100%',
        '&:hover': {
          cursor: 'pointer',
          ...(!isRequestDetailsVariant && {
            '& .addIcon': {
              display: 'flex',
            },
          }),
        },
      }}
    >
      {isRequestDetailsVariant ? (
        <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', width: '100%', pt: 1 }}>
          <Typography variant="HeaderSubtitleMediumRegular" color="secondary.main">
            {t('request-owners.add')}
          </Typography>
          <Add sx={{ width: 16, height: 16, color: 'secondary.main' }} />
        </Stack>
      ) : (
        <Stack className="addIcon" sx={{ display: 'none', alignItems: 'center', justifyContent: 'center' }}>
          <Add sx={{ ml: 1, width: 16, height: 16, color: 'secondary.main' }} />
        </Stack>
      )}
    </Stack>
  )
}

export const RequestOwner = ({ owner, requestId, variant }: RequestOwnerProps) => {
  const { t } = useTranslation('requests')
  const { user } = useAuthenticatedUser()
  const { mutate: updateRequestProperties } = useUpdateRequestProperties()
  const [open, setOpen] = useState(false)
  const [hoverRef, isHovered] = useHover()

  const updateOwner = async (updatedOwner: UserItem | null) => {
    updateRequestProperties({ requestId, ownerId: updatedOwner?.id ?? null })
    setOpen(false)
  }
  const editProjectOwner = () => {
    if (!user.isNonViewerAdmin) return
    setOpen(true)
  }
  const handleClickAway = () => setOpen(false)
  const name = owner?.name

  if (open) {
    return (
      <ClickAwayListener onClickAway={handleClickAway}>
        <Stack onClick={(event) => event.preventDefault()} sx={{ maxHeight: 40, minWidth: 200 }}>
          <UserAutocomplete autoFocus onChange={updateOwner} value={owner} />
        </Stack>
      </ClickAwayListener>
    )
  }
  if (name) {
    return (
      <Box
        ref={hoverRef}
        sx={{
          display: 'flex',
          overflow: 'hidden',
          justifyContent: 'space-between',
          alignItems: 'center',
          ...(user.isAdmin && {
            '&:hover': {
              cursor: 'pointer',
            },
          }),
        }}
        onClick={(event) => {
          event.preventDefault()
          editProjectOwner()
        }}
      >
        <ProfileBox sx={{ pt: 1 }} avatarVariant="circular" text={owner?.name} userId={owner?.id || null} />
        {isHovered && <Edit sx={{ ml: 2, width: 16, height: 16, color: 'neutralLight.700' }} />}
      </Box>
    )
  }
  if (user.isNonViewerAdmin) {
    return <AddProjectOwnerActionItem editProjectOwner={editProjectOwner} variant={variant} />
  }
  return <Typography variant="HeaderSubtitleMediumRegular">{t('request-owners.empty')}</Typography>
}
